import { Injectable, inject, Optional } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable, from, of, throwError, Subject } from "rxjs";
import { catchError, map, switchMap, tap } from "rxjs/operators";
import { AppserverInstallationCredentialsRepository } from "../credentials";
import { Capacitor } from "@capacitor/core";
import { PushNotifications, PushNotificationSchema, ActionPerformed } from "@capacitor/push-notifications";
import { Preferences } from "@capacitor/preferences";

export interface PushSubscriptionData {
    endpoint: string;
    // For native push (Capacitor): token gemmes direkte
    token?: string;
    userId?: string; // push-subscription-user-id fra serveren
}

export interface NotificationPayload {
    title: string;
    body: string;
    icon?: string;
    badge?: string;
    tag?: string;
    data?: any;
    actions?: NotificationAction[];
    requireInteraction?: boolean;
    silent?: boolean;
    timestamp?: number;
    antalDokumenter?: number; // Antal ulæste dokumenter fra backend
}

export interface NotificationAction {
    action: string;
    title: string;
    icon?: string;
}

@Injectable({ providedIn: "root" })
export class PushNotificationService {
    private readonly http = inject(HttpClient);
    private readonly installationCredentials = inject(AppserverInstallationCredentialsRepository, { optional: true });

    // Capacitor push notification subjects
    private readonly pushNotificationReceived$ = new Subject<NotificationPayload>();
    private readonly pushNotificationActionPerformed$ = new Subject<{ action: string; notification: NotificationPayload }>();

    constructor() {
        // Initialize Capacitor push notifications if available
        // Delay initialization to allow Firebase to initialize first
        if (Capacitor.isNativePlatform()) {
            // Use longer delay to ensure Firebase is ready
            setTimeout(() => {
                try {
                    this.initializeCapacitorPushNotifications();
                } catch (error) {
                    console.error("Push notifications initialization failed:", error);
                }
            }, 500);
        }
    }

    /**
     * Initializes Capacitor push notifications listeners
     */
    private initializeCapacitorPushNotifications(): void {
        // Tjek om plugin er tilgængelig (kan være undefined hvis ikke synkroniseret til iOS)
        if (typeof PushNotifications === "undefined" || !PushNotifications) {
            const platform = Capacitor.getPlatform();
            console.error(`PushNotifications plugin ikke tilgængelig på ${platform}`);
            return;
        }

        // Tjek om plugin metoder er tilgængelige
        if (typeof PushNotifications.addListener !== "function" || typeof PushNotifications.register !== "function") {
            const platform = Capacitor.getPlatform();
            console.error(`PushNotifications plugin ikke korrekt implementeret på ${platform}`);
            return;
        }

        // VIGTIGT: Vi sætter IKKE op en initialization listener for "registration" event
        // Dette forårsager race conditions - hvis initialization listeneren fanger eventet,
        // kommer det ikke igen når brugeren beder om permission
        // I stedet håndterer vi registration i requestCapacitorPushNotificationsAndRegisterOnAppServer()

        // On registration error (dette event kan vi lytte til globalt)
        try {
            PushNotifications.addListener("registrationError", (error) => {
                console.error("❌ Error on registration: " + JSON.stringify(error));
            });
        } catch (error) {
            console.error("Fejl ved opsætning af push notification error listener:", error);
        }

        // VIGTIGT: Vi kalder IKKE register() her i initialization
        // Dette forårsager at registration eventet fire før brugeren beder om permission
        // I stedet kalder vi register() kun når brugeren eksplicit beder om permission
        // Se requestCapacitorPushNotificationsAndRegisterOnAppServer() for korrekt flow

        // On push notification received
        PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
            const payload: NotificationPayload = {
                title: notification.title || "",
                body: notification.body || "",
                data: notification.data
            };
            this.pushNotificationReceived$.next(payload);
        });

        // On push notification action performed
        PushNotifications.addListener("pushNotificationActionPerformed", (action: ActionPerformed) => {
            const payload: NotificationPayload = {
                title: action.notification.title || "",
                body: action.notification.body || "",
                data: action.notification.data
            };
            this.pushNotificationActionPerformed$.next({
                action: action.actionId,
                notification: payload
            });
        });
    }

    /**
     * Tjekker om appen kører i Capacitor (native app)
     */
    public isCapacitor(): boolean {
        return Capacitor.isNativePlatform();
    }

    /**
     * Anmoder om tilladelse til push notifikationer og registrerer token direkte på app serveren
     * Understøtter kun Capacitor (native)
     */
    public requestNotificationPermissionAndRegisterOnAppServer(appServerUrl: string, forbrugerIds: string[]): Observable<PushSubscriptionData | null> {
        if (!this.isCapacitor()) {
            return of(null);
        }

        return this.requestCapacitorPushNotificationsAndRegisterOnAppServer(appServerUrl, forbrugerIds);
    }

    private async saveFcmToken(token: string | null | undefined): Promise<void> {
        if (!token || token.trim() === "") {
            return;
        }
        try {
            // Save both keys the service attempts to read
            await Preferences.set({ key: "firebase_fcm_token", value: token });
            await Preferences.set({ key: "capacitor-storage.firebase_fcm_token", value: token });
        } catch (error) {
            console.error("Fejl ved gemning af FCM token i Preferences:", error);
        }
    }

    /**
     * Anmoder om tilladelse til push notifikationer via Capacitor og registrerer på app serveren
     */
    private requestCapacitorPushNotificationsAndRegisterOnAppServer(appServerUrl: string, forbrugerIds: string[]): Observable<PushSubscriptionData | null> {
        // Tjek om brugeren tidligere har unsubscribed
        if (this.getUnsubscribeStatus()) {
            return of(null);
        }

        // Tjek om token allerede findes i localStorage (fra tidligere registration)
        const existingToken = localStorage.getItem("capacitor-push-token");
        if (existingToken && existingToken.trim() !== "") {
            const subscriptionData: PushSubscriptionData = {
                endpoint: `capacitor://push/${existingToken}`, // Custom endpoint format for Capacitor
                token: existingToken // Native push token
            };

            // Register on app server med eksisterende token
            return this.registerCapacitorTokenOnAppServer(appServerUrl, existingToken, forbrugerIds).pipe(
                tap(() => {
                    this.saveSubscriptionToLocalStorage(subscriptionData);
                    this.setLocalNotificationStatus(true);
                }),
                map(() => subscriptionData),
                catchError((error) => {
                    console.error("Fejl ved registrering af eksisterende Capacitor push token på app server:", error);
                    return of(null);
                })
            );
        }

        // Request permission via Capacitor
        return from(PushNotifications.requestPermissions()).pipe(
            switchMap((permissionStatus) => {
                if (permissionStatus.receive !== "granted") {
                    return of(null);
                }

                // Tjek om der allerede er et token i localStorage (fra tidligere registration eller initialization)
                const existingToken = localStorage.getItem("capacitor-push-token");
                if (existingToken && existingToken.trim() !== "") {
                    const subscriptionData: PushSubscriptionData = {
                        endpoint: `capacitor://push/${existingToken}`, // Custom endpoint format for Capacitor
                        token: existingToken // Native push token
                    };

                    // Register on app server med eksisterende token
                    return this.registerCapacitorTokenOnAppServer(appServerUrl, existingToken, forbrugerIds).pipe(
                        tap(() => {
                            this.saveSubscriptionToLocalStorage(subscriptionData);
                            this.setLocalNotificationStatus(true);
                        }),
                        map(() => subscriptionData),
                        catchError((error) => {
                            console.error("Fejl ved registrering af eksisterende Capacitor push token på app server:", error);
                            return of(null);
                        })
                    );
                }

                // Vent på token fra registration listener
                return new Observable<PushSubscriptionData | null>((observer) => {
                    let tokenListenerHandle: any = null;
                    let timeoutHandle: any = null;
                    let retryInterval: any = null;
                    let tokenReceived = false;
                    const startTime = Date.now();

                    // Hjælpefunktion til at håndtere token
                    const handleToken = async (token: string) => {
                        if (tokenReceived) {
                            return; // Allerede modtaget token
                        }
                        // Tjek om observeren allerede er closed (timeout allerede håndteret)
                        if (observer.closed) {
                            return; // Ignorer token hvis observeren allerede er closed
                        }
                        tokenReceived = true;

                        // Ryd op
                        if (timeoutHandle) {
                            clearTimeout(timeoutHandle);
                        }
                        if (retryInterval) {
                            clearInterval(retryInterval);
                        }
                        if (tokenListenerHandle) {
                            tokenListenerHandle.remove();
                        }

                        if (Capacitor.getPlatform() === "ios") {
                            try {
                                await this.saveFcmToken(token);

                                // Læs FCM token fra Preferences
                                let fcmToken: string | null = null;
                                
                                try {
                                    const result = await Preferences.get({ key: "firebase_fcm_token" });
                                    fcmToken = result?.value || null;
                                } catch (e) {
                                    // Prøv med namespace hvis standard key ikke virker
                                    try {
                                        const result = await Preferences.get({ key: "capacitor-storage.firebase_fcm_token" });
                                        fcmToken = result?.value || null;
                                    } catch (e2) {
                                        // Ignorer fejl
                                    }
                                }

                                if (fcmToken && fcmToken.trim() !== "") {
                                    token = fcmToken;
                                } else {
                                    console.error("❌ FCM token ikke fundet i Preferences");
                                    console.error("❌ Tjek at AppDelegate.swift gemmer FCM token med key 'firebase_fcm_token'");
                                    throw new Error("FCM token ikke fundet. Tjek at AppDelegate.swift gemmer FCM token korrekt.");
                                }
                            } catch (error) {
                                console.error("❌ Fejl ved læsning af FCM token fra Preferences:", error);
                                throw error;
                            }
                        }

                        const subscriptionData: PushSubscriptionData = {
                            endpoint: `capacitor://push/${token}`, // Custom endpoint format for Capacitor
                            token: token // Native push token
                        };

                        // Register on app server
                        this.registerCapacitorTokenOnAppServer(appServerUrl, token, forbrugerIds)
                            .pipe(
                                tap(() => {
                                    this.saveSubscriptionToLocalStorage(subscriptionData);
                                    this.setLocalNotificationStatus(true);
                                }),
                                map(() => subscriptionData),
                                catchError((error) => {
                                    console.error("Fejl ved registrering af Capacitor push token på app server:", error);
                                    return of(null);
                                })
                            )
                            .subscribe({
                                next: (data) => {
                                    if (!observer.closed) {
                                        // Send data til observeren når HTTP request returnerer
                                        // tokenReceived er allerede true på dette tidspunkt, så vi skal ikke tjekke det
                                        observer.next(data);
                                        observer.complete();
                                    }
                                },
                                error: (error) => {
                                    if (!observer.closed) {
                                        observer.error(error);
                                    }
                                }
                            });
                    };

                    // Tjek om addListener er tilgængelig
                    if (typeof PushNotifications.addListener !== "function") {
                        const platform = Capacitor.getPlatform();
                        console.error(`PushNotifications.addListener ikke tilgængelig på ${platform}`);
                        observer.next(null);
                        observer.complete();
                        return;
                    }

                    // VIGTIGT: Tilføj listener FØR register() for at undgå race condition
                    // På iOS kan registration eventet fire med det samme
                    PushNotifications.addListener("registration", async (token) => {
                        await handleToken(token.value);
                    })
                        .then((handle) => {
                            tokenListenerHandle = handle;

                            // Nu hvor listeneren er sat op, kan vi kalde register()
                            // Register for push notifications (token vil blive modtaget via listener)
                            try {
                                if (typeof PushNotifications.register !== "function") {
                                    const platform = Capacitor.getPlatform();
                                    console.error(`PushNotifications.register ikke tilgængelig på ${platform}`);
                                    if (tokenListenerHandle) {
                                        tokenListenerHandle.remove();
                                    }
                                    observer.next(null);
                                    observer.complete();
                                    return;
                                }
                                PushNotifications.register()
                                    .then(() => {
                                        // Registration initiated
                                    })
                                    .catch((error) => {
                                        console.error("❌ PushNotifications.register() fejlede:", error);
                                    });
                            } catch (error) {
                                const errorMessage = error?.message || String(error);
                                if (errorMessage.includes("not implemented") || errorMessage.includes("not available")) {
                                    const platform = Capacitor.getPlatform();
                                    console.error(`PushNotifications plugin ikke implementeret for ${platform}`);
                                } else {
                                    console.error("Push notifications registration failed:", error);
                                }
                                if (tokenListenerHandle) {
                                    tokenListenerHandle.remove();
                                }
                                observer.next(null);
                                observer.complete();
                            }
                        })
                        .catch((error) => {
                            console.error("Fejl ved opsætning af push notification listener:", error);
                            observer.next(null);
                            observer.complete();
                        });

                    // Polling af localStorage er ikke længere nødvendig
                    // Vi har fjernet initialization listeneren for at undgå race conditions
                    // Token tjekkes allerede FØR vi sætter listener op (se linje 318-342)

                    // Retry mekanisme: Prøv at kalde register() igen efter 5 sekunder hvis token ikke er kommet
                    let retryCount = 0;
                    const maxRetries = 2;
                    retryInterval = setInterval(() => {
                        if (tokenReceived) {
                            clearInterval(retryInterval);
                            return;
                        }
                        retryCount++;
                        if (retryCount > maxRetries) {
                            clearInterval(retryInterval);
                            return;
                        }
                        try {
                            if (typeof PushNotifications.register === "function") {
                                PushNotifications.register()
                                    .then(() => {
                                        // Retry initiated
                                    })
                                    .catch((error) => {
                                        console.error("Push notifications registration retry failed:", error);
                                    });
                            }
                        } catch (error) {
                            console.error("Fejl ved retry af push notifications registration:", error);
                        }
                    }, 5000); // Prøv hver 5. sekund

                    // Timeout after 20 seconds (øget fra 10 sekunder for at give iOS mere tid)
                    timeoutHandle = setTimeout(() => {
                        if (tokenReceived) {
                            return; // Token allerede modtaget
                        }
                        const elapsedTime = ((Date.now() - startTime) / 1000).toFixed(1);
                        console.error(`Push notifications registration timeout efter ${elapsedTime} sekunder`);

                        if (tokenListenerHandle) {
                            tokenListenerHandle.remove();
                        }
                        if (retryInterval) {
                            clearInterval(retryInterval);
                        }
                        if (!observer.closed) {
                            // Tjek localStorage en sidste gang før timeout
                            const storedToken = localStorage.getItem("capacitor-push-token");
                            if (storedToken && storedToken.trim() !== "") {
                                handleToken(storedToken);
                            } else {
                                console.error("❌ INGEN TOKEN fundet - hverken via event eller localStorage");
                                console.error("❌ Mulige årsager:");
                                console.error("   1. Firebase/APNs er ikke korrekt konfigureret i native projektet");
                                console.error("   2. Registration eventet blev fired FØR vores listener blev sat op (race condition)");
                                console.error("   3. Network connectivity problemer - Firebase kan ikke nås");
                                console.error("   4. iOS APNs er ikke konfigureret korrekt (certifikat/provisioning)");
                                console.error("❌ Subscription bliver IKKE sendt til appserver - ingen token tilgængelig");
                                observer.next(null);
                                observer.complete();
                            }
                        }
                    }, 20000); // Øget til 20 sekunder
                });
            }),
            catchError((error) => {
                console.error("Fejl ved request af push notification permissions:", error);
                return of(null);
            })
        );
    }

    /**
     * Registrerer Capacitor push token på app serveren
     */
    private registerCapacitorTokenOnAppServer(appServerUrl: string, token: string, forbrugerIds: string[]): Observable<void> {
        // Hent credentials for at få sikkerhedsparametre
        if (!this.installationCredentials) {
            console.error("❌ InstallationCredentials ikke tilgængelig - kan ikke registrere Capacitor token");
            return throwError(() => new Error("InstallationCredentials ikke tilgængelig"));
        }
        const credentials = this.installationCredentials.hent();
        if (!credentials?.cryptId) {
            console.error("❌ Mangler credentials (cryptId) - kan ikke registrere Capacitor token");
            return throwError(() => new Error("Mangler credentials for at registrere Capacitor token"));
        }

        // Ekstraher værkets navn fra appServerUrl (f.eks. "http://localhost:20081/gelsted/" -> "gelsted")
        // og byg endpoint med værkets navn: "http://localhost:20081/gelsted/api/PushNotificationSubscribe"
        const urlObj = new URL(appServerUrl);
        const pathParts = urlObj.pathname.split("/").filter((p) => p); // Fjern tomme strings
        const vaerkNavn = pathParts[0] || ""; // Første del af path er værkets navn (f.eks. "gelsted")

        // Valider at vaerkNavn ikke er tomt
        if (!vaerkNavn) {
            console.error("❌ Kunne ikke ekstrahere værkets navn fra appServerUrl:", appServerUrl);
            return throwError(() => new Error(`Ugyldig appServerUrl: mangler værkets navn i path. URL: ${appServerUrl}`));
        }

        // Byg endpoint med værkets navn: /gelsted/api/PushNotificationSubscribe
        // Tilføj id og unr parametre for sikkerhed
        const appServerEndpoint = `${urlObj.protocol}//${urlObj.host}/${vaerkNavn}/api/PushNotificationSubscribe?id=${credentials.cryptId}&unr=${credentials.ejendomNr}`;

        // forbrugerIds kommer allerede i formatet "appServerUrl;id" fra notification-permission-request.component.ts
        // Tjek om de allerede er i det rigtige format, ellers konverter dem
        const processedForbrugerIds = forbrugerIds.map((id) => {
            // Hvis id allerede indeholder appServerUrl, brug det direkte
            if (id.includes(";")) {
                return id;
            }
            // Ellers tilføj appServerUrl
            return `${appServerUrl};${id}`;
        });

        // Valider at token ikke er tom
        if (!token || token.trim() === "") {
            console.error("❌ Token er tom eller ugyldig:", token);
            return throwError(() => new Error("Token er tom eller ugyldig"));
        }

        // Valider at platform er sat
        const platform = Capacitor.getPlatform();
        if (!platform || (platform !== "ios" && platform !== "android")) {
            console.error("❌ Platform er ugyldig:", platform);
            return throwError(() => new Error(`Ugyldig platform: ${platform}`));
        }

        // Valider at forbrugerIds ikke er tom
        if (!processedForbrugerIds || processedForbrugerIds.length === 0) {
            console.error("❌ forbrugerIds er tom eller ugyldig:", processedForbrugerIds);
            return throwError(() => new Error("forbrugerIds er tom eller ugyldig"));
        }

        const body = {
            token: token.trim(),
            platform: platform,
            forbrugerIds: processedForbrugerIds
        };

        // Konverter app server URL til proxy URL hvis nødvendigt (for local development)
        // http://localhost:20081/gelsted/api/PushNotificationSubscribe?id=... -> /appserver/gelsted/api/PushNotificationSubscribe?id=...
        const proxyUrl = this.convertAppServerUrlToProxy(appServerEndpoint);

        // Valider at body kan serialiseres til JSON
        let bodyJson: string;
        try {
            bodyJson = JSON.stringify(body);
            if (!bodyJson || bodyJson === "{}") {
                console.error("❌ Request body er tom efter JSON serialisering");
                return throwError(() => new Error("Request body er tom efter JSON serialisering"));
            }
        } catch (jsonError) {
            console.error("❌ Fejl ved JSON serialisering af request body:", jsonError);
            return throwError(() => new Error("Fejl ved JSON serialisering af request body"));
        }

        return this.http
            .post<void>(proxyUrl, body, {
                responseType: "json",
                observe: "response",
                headers: {
                    "Content-Type": "application/json"
                }
            })
            .pipe(
                tap(() => {
                    // Success
                }),
                map(() => void 0),
                catchError((error) => {
                    console.error("❌ Fejl ved registrering af Capacitor push token:", error);
                    console.error("❌ Error details:", {
                        status: error.status,
                        statusText: error.statusText,
                        url: error.url,
                        message: error.message,
                        error: error.error
                    });
                    // Hvis det er en parsing fejl, kan det være at serveren returnerer HTML eller tomt svar
                    if (error.name === "HttpErrorResponse" && error.error instanceof ProgressEvent) {
                        console.error("⚠️ Server returnerede ikke-JSON response (måske HTML error page eller tomt svar)");
                    }
                    // Hvis det er en 500 fejl, kan det være at Delphi koden crasher ved JSON parsing
                    if (error.status === 500) {
                        console.error("⚠️ Server returnerede 500 fejl - Delphi koden crasher sandsynligvis ved JSON parsing");
                        console.error("⚠️ Dette kan skyldes at Delphi koden ikke understøtter native push tokens endnu");
                        console.error("⚠️ Delphi koden skal understøtte native push format (token, platform, forbrugerIds)");
                    }
                    return throwError(() => error);
                })
            );
    }

    /**
     * Anmoder om tilladelse til push notifikationer og registrerer subscription (LEGACY)
     * @deprecated Brug requestNotificationPermissionAndRegisterOnAppServer i stedet
     */
    public requestNotificationPermission(_forbrugerIds?: string[]): Observable<PushSubscriptionData | null> {
        return of(null);
    }

    /**
     * Opretter push subscription direkte via Push API (uden Service Worker)
     * Bruges i development mode når Service Worker ikke er tilgængelig
     */
    private subscribeToPushNotificationsDirectly(_forbrugerIds?: string[]): Observable<PushSubscriptionData | null> {
        return of(null);
    }

    /**
     * Abonnerer på push notifikationer og registrerer direkte på app serveren (med Service Worker)
     */
    private subscribeToPushNotificationsAndRegisterOnAppServer(_appServerUrl: string, _forbrugerIds: string[]): Observable<PushSubscriptionData> {
        return throwError(() => new Error("PWA push er fjernet - bruger kun Capacitor"));
    }

    /**
     * Abonnerer på push notifikationer (med Service Worker) - LEGACY
     * @deprecated Brug subscribeToPushNotificationsAndRegisterOnAppServer i stedet
     */
    private subscribeToPushNotifications(_forbrugerIds?: string[]): Observable<PushSubscriptionData> {
        return throwError(() => new Error("PWA push er fjernet - bruger kun Capacitor"));
    }

    /**
     * Registrerer subscription direkte på app serveren (ikke gennem push notification serveren)
     */
    public registerSubscriptionOnAppServer(
        appServerUrl: string,
        subscriptionData: PushSubscriptionData,
        forbrugerIds: string[] = []
    ): Observable<{ success: boolean; message: string; userId?: string }> {
        // Hent credentials for at få sikkerhedsparametre
        if (!this.installationCredentials) {
            console.error("❌ InstallationCredentials ikke tilgængelig - kan ikke oprette subscription");
            return throwError(() => new Error("InstallationCredentials ikke tilgængelig"));
        }
        const credentials = this.installationCredentials.hent();
        if (!credentials?.cryptId) {
            console.error("❌ Mangler credentials (cryptId) - kan ikke oprette subscription");
            return throwError(() => new Error("Mangler credentials for at oprette subscription"));
        }

        // Ekstraher værkets navn fra appServerUrl (f.eks. "http://localhost:20081/gelsted/" -> "gelsted")
        // og byg endpoint med værkets navn: "http://localhost:20081/gelsted/api/PushNotificationSubscribe"
        const urlObj = new URL(appServerUrl);
        const pathParts = urlObj.pathname.split("/").filter((p) => p); // Fjern tomme strings
        const vaerkNavn = pathParts[0] || ""; // Første del af path er værkets navn (f.eks. "gelsted")

        // Valider at vaerkNavn ikke er tomt
        if (!vaerkNavn) {
            console.error("❌ Kunne ikke ekstrahere værkets navn fra appServerUrl:", appServerUrl);
            return throwError(() => new Error(`Ugyldig appServerUrl: mangler værkets navn i path. URL: ${appServerUrl}`));
        }

        // Byg endpoint med værkets navn: /gelsted/api/PushNotificationSubscribe
        // Tilføj id og unr parametre for sikkerhed
        const appServerEndpoint = `${urlObj.protocol}//${urlObj.host}/${vaerkNavn}/api/PushNotificationSubscribe?id=${credentials.cryptId}&unr=${credentials.ejendomNr}`;

        const body = {
            subscription: subscriptionData,
            forbrugerIds: forbrugerIds
        };

        // Konverter app server URL til proxy URL hvis nødvendigt (for local development)
        // http://localhost:20081/gelsted/api/PushNotificationSubscribe?id=... -> /appserver/gelsted/api/PushNotificationSubscribe?id=...
        const proxyUrl = this.convertAppServerUrlToProxy(appServerEndpoint);

        return this.http
            .post<{ success: boolean; message: string; userId?: string }>(proxyUrl, body, {
                responseType: "json",
                observe: "response"
            })
            .pipe(
                map((response) => response.body || { success: false, message: "Ingen response fra server" }),
                tap((response) => {
                    // Gem userId fra app server response
                    if (response.userId) {
                        this.saveCurrentUserId(response.userId);
                    }
                }),
                catchError((error) => {
                    console.error("❌ Fejl ved registrering af subscription på app server:", error);
                    // Hvis det er en parsing fejl, kan det være at serveren returnerer HTML eller tomt svar
                    if (error.name === "HttpErrorResponse" && error.error instanceof ProgressEvent) {
                        console.error("⚠️ Server returnerede ikke-JSON response (måske HTML error page eller tomt svar)");
                        console.error("⚠️ Dette kan ske hvis serveren returnerer en HTML error page i stedet for JSON");
                    }
                    // Log mere detaljeret fejl information
                    if (error.error) {
                        console.error("⚠️ Error details:", {
                            status: error.status,
                            statusText: error.statusText,
                            url: error.url,
                            errorType: typeof error.error,
                            errorPreview: typeof error.error === "string" ? error.error.substring(0, 200) : error.error
                        });
                    }
                    return throwError(() => error);
                })
            );
    }

    /**
     * Tjekker om installation har notifikationer aktiveret på appserveren
     * Bruger token fra lokal subscription til at identificere subscription på appserveren
     */
    public checkNotificationStatusOnAppServer(appServerUrl: string): Observable<{ enabled: boolean; userId?: string }> {
        // Hent credentials for at få sikkerhedsparametre
        if (!this.installationCredentials) {
            console.error("❌ InstallationCredentials ikke tilgængelig - kan ikke tjekke notifikationsstatus");
            return throwError(() => new Error("InstallationCredentials ikke tilgængelig"));
        }
        const credentials = this.installationCredentials.hent();
        if (!credentials?.cryptId) {
            console.error("❌ Mangler credentials (cryptId) - kan ikke tjekke notifikationsstatus");
            return throwError(() => new Error("Mangler credentials for at tjekke notifikationsstatus"));
        }

        // Hent lokal subscription for at få token (bruges til at identificere subscription på appserveren)
        const localSubscription = this.getSubscriptionFromLocalStorage();

        const token = localSubscription?.token;

        // Hvis der ikke er en lokal subscription (token), er der ingen grund til at tjekke appserveren
        // Der er ingen subscription på denne device, så vi returnerer direkte enabled: false
        if (!token) {
            return of({ enabled: false });
        }

        // Ekstraher værkets navn fra appServerUrl (f.eks. "http://localhost:20081/gelsted/" -> "gelsted")
        // og byg endpoint med værkets navn: "http://localhost:20081/gelsted/api/PushNotificationStatus"
        const urlObj = new URL(appServerUrl);
        const pathParts = urlObj.pathname.split("/").filter((p) => p); // Fjern tomme strings
        const vaerkNavn = pathParts[0] || ""; // Første del af path er værkets navn (f.eks. "gelsted")

        // Valider at vaerkNavn ikke er tomt
        if (!vaerkNavn) {
            console.error("❌ Kunne ikke ekstrahere værkets navn fra appServerUrl:", appServerUrl);
            return of({ enabled: false }); // Returner false ved fejl i stedet for at kaste fejl
        }

        // Byg endpoint med værkets navn: /gelsted/api/PushNotificationStatus
        // id og unr sendes direkte i URL'en for at bevare eksisterende formatering (som andre dele af app serveren forventer)
        // token sendes i request body for at undgå URL encoding problemer (HttpParams encoder + til %2B som Delphi ikke decoder)
        const baseEndpoint = `${urlObj.protocol}//${urlObj.host}/${vaerkNavn}/api/PushNotificationStatus?id=${credentials.cryptId}&unr=${credentials.ejendomNr}`;

        // Konverter app server URL til proxy URL hvis nødvendigt (for local development)
        const proxyUrl = this.convertAppServerUrlToProxy(baseEndpoint);

        // Send token i request body for at undgå URL encoding problemer
        // Delphi koden skal læse token fra Content (JSON body) i stedet for Params
        const requestBody = { token: token };

        return this.http
            .post<{ enabled: boolean; userId?: string }>(proxyUrl, requestBody, {
                responseType: "json"
            })
            .pipe(
                tap((response) => {
                    // Response processed silently
                }),
                catchError((error) => {
                    console.error("❌ Fejl ved tjek af notifikationsstatus på app server:", error);
                    // Hvis det er en parsing fejl eller 404, betyder det sandsynligvis at endpoint ikke findes eller ikke er aktiveret
                    // Returner enabled: false ved fejl, så permission request kan vises
                    return of({ enabled: false });
                })
            );
    }

    /**
     * Konverterer app server URL til proxy URL for local development
     * http://localhost:20081/gelsted/api/PushNotificationSubscribe -> /appserver/gelsted/api/PushNotificationSubscribe
     */
    private convertAppServerUrlToProxy(appServerUrl: string): string {
        try {
            const url = new URL(appServerUrl);
            const isLocalhost = url.hostname === "localhost" || url.hostname === "127.0.0.1";
            const localhostPattern = /^https?:\/\/(localhost|127\.0\.0\.1):\d+\/(.+)$/i;

            if (isLocalhost && appServerUrl.match(localhostPattern)) {
                const match = appServerUrl.match(localhostPattern);
                if (match && match[2]) {
                    const path = match[2];
                    return `/appserver/${path}`;
                }
            }
        } catch (error) {
            // Hvis URL parsing fejler, returner original URL
        }
        return appServerUrl;
    }

    // Legacy PWA subscription registration fjernet (kun Capacitor)

    /**
     * Afmelder push notifikationer (kun Capacitor)
     */
    public unsubscribeFromNotifications(): Observable<void> {
        this.removeSubscriptionFromLocalStorage();
        this.setLocalNotificationStatus(false);
        return of(void 0);
    }

    /**
     * Afmelder subscription direkte fra app serveren
     */
    public unregisterSubscriptionFromAppServer(appServerUrl: string, userId: string): Observable<void> {
        // Hent credentials for at få sikkerhedsparametre
        if (!this.installationCredentials) {
            console.error("❌ InstallationCredentials ikke tilgængelig - kan ikke fjerne subscription");
            return throwError(() => new Error("InstallationCredentials ikke tilgængelig"));
        }
        const credentials = this.installationCredentials.hent();
        if (!credentials?.cryptId) {
            console.error("❌ Mangler credentials (cryptId) - kan ikke fjerne subscription");
            return throwError(() => new Error("Mangler credentials for at fjerne subscription"));
        }

        // Ekstraher værkets navn fra appServerUrl (f.eks. "http://localhost:20081/gelsted/" -> "gelsted")
        // og byg endpoint med værkets navn: "http://localhost:20081/gelsted/api/PushNotificationUnsubscribe"
        const urlObj = new URL(appServerUrl);
        const pathParts = urlObj.pathname.split("/").filter((p) => p); // Fjern tomme strings
        const vaerkNavn = pathParts[0] || ""; // Første del af path er værkets navn (f.eks. "gelsted")

        // Byg endpoint med værkets navn: /gelsted/api/PushNotificationUnsubscribe
        // Tilføj id og unr parametre for sikkerhed
        const appServerEndpoint = `${urlObj.protocol}//${urlObj.host}/${vaerkNavn}/api/PushNotificationUnsubscribe?id=${credentials.cryptId}&unr=${credentials.ejendomNr}`;

        const requestBody = {
            userId: userId
        };

        // Konverter app server URL til proxy URL hvis nødvendigt (for local development)
        // http://localhost:20081/gelsted/api/PushNotificationUnsubscribe?id=... -> /appserver/gelsted/api/PushNotificationUnsubscribe?id=...
        const proxyUrl = this.convertAppServerUrlToProxy(appServerEndpoint);

        return this.http.delete<void>(proxyUrl, { body: requestBody }).pipe(
            tap(() => {
                // Unsubscribe successful
            }),
            catchError((error) => {
                console.error("❌ Fejl ved afmelding fra app server:", error);
                return throwError(() => error);
            })
        );
    }

    // Web Push unsubscribe er fjernet (kun Capacitor)

    /**
     * Tjekker om notifikationer er aktiveret (lokal status)
     */
    public isNotificationEnabled(): boolean {
        return this.getLocalNotificationStatus();
    }

    /**
     * Henter lokal notifikationsstatus
     */
    private getLocalNotificationStatus(): boolean {
        const status = localStorage.getItem("push-notifications-enabled");
        return status === "true";
    }

    /**
     * Gemmer lokal notifikationsstatus
     */
    private setLocalNotificationStatus(enabled: boolean): void {
        localStorage.setItem("push-notifications-enabled", enabled.toString());

        // Hvis notifikationer deaktiveres, fjern også userId
        if (!enabled) {
            localStorage.removeItem("push-subscription-user-id");
        }
    }

    /**
     * Henter unsubscribe status
     */
    private getUnsubscribeStatus(): boolean {
        const status = localStorage.getItem("push-notifications-unsubscribed");
        return status === "true";
    }

    /**
     * Gemmer unsubscribe status
     */
    private setUnsubscribeStatus(unsubscribed: boolean): void {
        localStorage.setItem("push-notifications-unsubscribed", unsubscribed.toString());
    }

    /**
     * Nulstiller unsubscribe status (når brugeren manuelt aktiverer igen)
     */
    public resetUnsubscribeStatus(): void {
        this.setUnsubscribeStatus(false);
    }

    /**
     * Henter nuværende subscription data
     */
    public getCurrentSubscription(): PushSubscriptionData | null {
        return this.getSubscriptionFromLocalStorage();
    }

    // Web Push helper fjernet (kun Capacitor)

    /**
     * Gemmer subscription data i localStorage
     */
    private saveSubscriptionToLocalStorage(subscriptionData: PushSubscriptionData): void {
        localStorage.setItem("push-subscription", JSON.stringify(subscriptionData));
    }

    /**
     * Henter subscription data fra localStorage
     */
    private getSubscriptionFromLocalStorage(): PushSubscriptionData | null {
        const stored = localStorage.getItem("push-subscription");
        return stored ? JSON.parse(stored) : null;
    }

    /**
     * Fjerner subscription data fra localStorage
     */
    private removeSubscriptionFromLocalStorage(): void {
        localStorage.removeItem("push-subscription");
        localStorage.removeItem("push-subscription-user-id");

        // Gem at brugeren har unsubscribed
        this.setUnsubscribeStatus(true);
    }

    /**
     * Rydder bruger-specifik subscription metadata ved logout.
     * Bevarer FCM-token (device-specifikt), men fjerner hvilken bruger/installation det er knyttet til.
     * Forhindrer forkert popup-opførsel når bruger B logger ind efter bruger A uden at lukke appen.
     */
    public clearUserSpecificSubscriptionMetadataOnLogout(): void {
        localStorage.removeItem("push-subscription-installation-key");
        localStorage.removeItem("push-subscription-user-id");
    }

    /**
     * Henter nuværende bruger ID fra localStorage
     */
    public getCurrentUserId(): string | null {
        const stored = localStorage.getItem("push-subscription-user-id");
        return stored || null;
    }

    /**
     * Gemmer bruger ID i localStorage
     */
    private saveCurrentUserId(userId: string): void {
        localStorage.setItem("push-subscription-user-id", userId);
    }

    /**
     * Tjekker om push notifications kan bruges (kun Capacitor)
     */
    public canUsePushNotifications(): boolean {
        return this.isCapacitor();
    }

    /**
     * Lytter til push notifikation events (kun Capacitor)
     */
    public onPushNotification(): Observable<NotificationPayload> {
        if (!this.isCapacitor()) {
            return new Observable((observer) => observer.complete());
        }
        return this.pushNotificationReceived$.asObservable();
    }

    /**
     * Lytter til subscription events (kun Capacitor)
     */
    public onSubscriptionChange(): Observable<PushSubscriptionData | null> {
        return new Observable((observer) => observer.complete());
    }

    /**
     * Observerer service worker status (ikke relevant for Capacitor)
     */
    public onServiceWorkerStatus(): Observable<boolean> {
        return of(false);
    }

}
